import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

// stake gate (demo)
function useStakeGate() {
  const [isStaked, setIsStaked] = useState<boolean>(false);
  useEffect(() => {
    const v = localStorage.getItem('copycat_is_staked');
    setIsStaked(v === '1');
  }, []);
  return { isStaked };
}

// Spinning number ring canvas
const NumberRing: React.FC<{ spinning: boolean }> = ({ spinning }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const angleRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = Math.min(window.innerWidth, 700);
      canvas.height = 320;
    };
    resize();
    window.addEventListener('resize', resize);

    const draw = () => {
      if (!canvas || !ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);

      const radius = Math.min(canvas.width, canvas.height) / 2 - 20;
      const count = 24;
      angleRef.current += spinning ? 0.04 : 0.005;

      for (let i = 0; i < count; i++) {
        const a = angleRef.current + (i * (Math.PI * 2)) / count;
        const x = Math.cos(a) * radius;
        const y = Math.sin(a) * radius * 0.5; // ellipse for pseudo-3D

        const scale = 0.6 + (y / (radius * 0.5)) * 0.4; // depth scaling
        const opacity = 0.5 + (y / (radius * 0.5)) * 0.5; // brighter when closer

        const n = (i * 7 + Math.floor(angleRef.current * 10)) % 10; // changing number

        ctx.save();
        ctx.globalAlpha = Math.max(0.15, opacity);
        ctx.fillStyle = '#10b981';
        ctx.font = `${Math.floor(36 * scale)}px JetBrains Mono, monospace`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(n), x, y);
        ctx.restore();
      }

      ctx.restore();
      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [spinning]);

  return <canvas ref={canvasRef} className="w-full" />;
};

const LotteryPlay: React.FC = () => {
  const { isStaked } = useStakeGate();
  const navigate = useNavigate();
  const [spinning, setSpinning] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);
  const [roundInfo, setRoundInfo] = useState<{ id: number; mm: string; ss: string }>({ id: 0, mm: '00', ss: '00' });

  useEffect(() => {
    if (!isStaked) {
      // redirect to landing to stake
      const t = setTimeout(() => navigate('/lottery'), 1200);
      return () => clearTimeout(t);
    }
  }, [isStaked, navigate]);

  // Round ticker: 30-minute slots, global continuous
  useEffect(() => {
    const compute = () => {
      const nowMs = Date.now();
      const roundLenMs = 30 * 60 * 1000;
      const roundId = Math.floor(nowMs / roundLenMs); // epoch-based round id
      const nextMs = (roundId + 1) * roundLenMs;
      const remain = Math.max(0, Math.floor((nextMs - nowMs) / 1000));
      const mm = String(Math.floor(remain / 60)).padStart(2, '0');
      const ss = String(remain % 60).padStart(2, '0');
      setRoundInfo({ id: roundId, mm, ss });
    };
    compute();
    const id = setInterval(compute, 1000);
    return () => clearInterval(id);
  }, []);

  const requestRandomDemo = async () => {
    // This is a placeholder. In the real flow, you'd call the Entropy provider request
    // and listen for the callback event. We'll simulate a delay and result.
    setSpinning(true);
    setLastResult(null);
    await new Promise((r) => setTimeout(r, 2200));
    const val = Math.floor(Math.random() * 1_000_000);
    setLastResult(`#${val.toString().padStart(6, '0')}`);
    setSpinning(false);
  };

  return (
    <div className="h-full bg-black text-green-400 flex flex-col">
      <div className="p-6 border-b border-green-500/30 bg-[#030306]">
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-green-300 font-bold text-xl">Lottery Play (Demo)</div>
          <span className={`text-xs px-2 py-1 rounded border ${isStaked ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-300' : 'border-yellow-400/40 bg-yellow-500/10 text-yellow-200'}`}>
            {isStaked ? 'Eligible' : 'Not Eligible'}
          </span>
          <span className="text-xs px-2 py-1 rounded border border-green-500/30 bg-green-500/10 text-green-300">
            Round #{roundInfo.id} • Next in {roundInfo.mm}:{roundInfo.ss}
          </span>
        </div>
        <div className="text-green-500/70 text-sm">Stake-gated • Pyth Entropy powered (coming up)</div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div className="p-5 rounded-lg border border-green-500/30 bg-green-500/5">
            <div className="text-sm text-green-500/70">How it works</div>
            <ul className="mt-2 text-green-300 space-y-1 text-sm list-disc list-inside">
              <li>Request a randomness from Pyth Entropy provider</li>
              <li>Provider reveals, contract emits result via callback</li>
              <li>Lucky draws determine winners for the prize pool</li>
            </ul>
          </div>

          <div className="p-5 rounded-lg border border-green-500/30 bg-green-500/5">
            <div className="text-sm text-green-500/70">Status</div>
            <div className="text-green-300 mt-2">
              {spinning ? 'Requesting randomness...' : lastResult ? `Last Result: ${lastResult}` : 'No spins yet'}
            </div>
            <div className="mt-4 flex gap-3">
              <Button onClick={requestRandomDemo} disabled={spinning || !isStaked} className="bg-green-600/30 border border-green-500/40 text-green-300 hover:bg-green-500/20 disabled:opacity-60">
                {spinning ? 'Spinning...' : 'Request Randomness'}
              </Button>
              <Button onClick={() => navigate('/lottery/winners')} variant="outline" className="border-green-500/40 text-green-400/80 hover:bg-green-500/10">
                See Winners
              </Button>
            </div>
          </div>
        </div>

        {/* Animated machine */}
        <div className="p-5 rounded-lg border border-green-500/30 bg-[#030306]">
          <div className="text-sm text-green-500/70 mb-2">Entropy Visualizer</div>
          <div className="rounded-lg border border-green-500/20 bg-black/40 p-4">
            <NumberRing spinning={spinning} />
            <div className="mt-4 text-center text-green-300 text-sm">
              {spinning ? 'Verifying provider reveal...' : 'Idle'}
            </div>
          </div>
          <div className="mt-6 grid grid-cols-3 gap-3 text-center">
            {['Fair', 'Verifiable', 'On-Chain'].map((t, i) => (
              <div key={i} className="p-3 rounded-md border border-green-500/20 bg-green-500/5">
                <div className="text-xs text-green-500/70">{t}</div>
                <div className="text-green-300 font-semibold mt-1">OK</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LotteryPlay;
